/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: BasicConfigurationSection.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Basic configuration section for AI Profiles form

 🔧 Notes:
    - Handles profile name, provider, type, and model selection
    - Provider-agnostic configuration

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { Bot } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import Select from '../../../components/ui/select';
import Combobox from '../../../components/ui/Combobox';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { AiProfile, AiProvider } from '../types';

interface BasicConfigurationSectionProps {
  formData: AiProfile;
  providers: AiProvider[];
  availableTypes: string[];
  availableModels: string[];
  loadingTypes: boolean;
  loadingModels: boolean;
  selectedProvider: AiProvider | null;
  errors: Record<string, string>;
  isEditMode: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInputChange: (field: string, value: any) => void;
}

// Helper to wrap labels with tooltips consistently
const TooltipLabel = ({ htmlFor, label, tooltip, required }: { htmlFor: string; label: string; tooltip?: string; required?: boolean }) => {
  if (!tooltip) {
    return (
      <Label htmlFor={htmlFor} className="text-left" required={required}>
        {label}
      </Label>
    );
  }
  return (
    <SimpleTooltip content={tooltip}>
      <Label htmlFor={htmlFor} className="text-left" required={required}>
        {label}
      </Label>
    </SimpleTooltip>
  );
};

export default function BasicConfigurationSection({
  formData,
  providers,
  availableTypes,
  availableModels,
  loadingTypes,
  loadingModels,
  selectedProvider,
  errors,
  isEditMode,
  onInputChange
}: BasicConfigurationSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('aiProfiles.sections.basicConfiguration')} icon={<Bot />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
        {/* Row 1: Profile Name and Enabled */}
        <TooltipLabel
          htmlFor="profile_name"
          label={t('aiProfiles.fields.profileName.label')}
          tooltip={t('aiProfiles.fields.profileName.tooltip')}
          required
        />
        <div className="col-span-1">
          <Input
            id="profile_name"
            type="text"
            value={formData.profile_name || ''}
            onChange={(e) => onInputChange('profile_name', e.target.value)}
            placeholder={t('aiProfiles.fields.profileName.placeholder')}
          />
        </div>
        <TooltipLabel
          htmlFor="status"
          label={t('common.enabled')}
          tooltip={t('aiProfiles.fields.status.tooltip')}
        />
        <div className="col-span-1">
          <Toggle
            value={formData.status}
            onChange={(checked) => onInputChange('status', checked)}
          />
        </div>
        {errors.profile_name && <span className="col-span-4 text-sm text-red-500">{errors.profile_name}</span>}

        {/* Row 2: Provider & Type */}
        <TooltipLabel
          htmlFor="provider_id"
          label={t('aiProfiles.fields.provider.label')}
          tooltip={t('aiProfiles.fields.provider.tooltip')}
          required
        />
        <div className="col-span-1">
          <Select
            value={formData.provider_id?.toString() || ''}
            onValueChange={(value) => onInputChange('provider_id', value || undefined)}
            options={providers.map(provider => ({
              value: provider.id.toString(),
              label: `${provider.name} (${provider.provider})`
            }))}
            placeholder={t('aiProfiles.fields.provider.placeholder')}
            disabled={isEditMode}
          />
        </div>
        {selectedProvider?.provider !== 'azure' ? (
          <>
            <TooltipLabel
              htmlFor="type"
              label={t('aiProfiles.labels.type')}
              tooltip={t('aiProfiles.fields.type.tooltip', 'Select the core AI role for this profile (e.g. general chat, TTS, STT).')}
              required
            />
            <div className="col-span-1">
              <Select
                value={formData.type}
                onValueChange={(value) => onInputChange('type', value)}
                options={availableTypes.map(type => {
                  const typeLabels: Record<string, string> = {
                    general: t('aiProfiles.types.general', 'General'),
                    tts: t('aiProfiles.types.tts', 'TTS'),
                    stt: t('aiProfiles.types.stt', 'STT'),
                    whisper: t('aiProfiles.types.whisper', 'Whisper'),
                    agent_realtime: t('aiProfiles.types.agentRealtime', 'Agent Realtime'),
                    embeddings: t('aiProfiles.types.embeddings', 'Embeddings'),
                    image: t('aiProfiles.types.image', 'Image')
                  };
                  return {
                    value: type,
                    label: typeLabels[type] || (type.charAt(0).toUpperCase() + type.slice(1))
                  };
                })}
                placeholder={loadingTypes ? t('aiProfiles.modals.loadingTypes') : t('aiProfiles.modals.selectType')}
                disabled={isEditMode || loadingTypes || !formData.provider_id}
              />
            </div>
          </>
        ) : (
          <div className="col-span-2"></div>
        )}
        {errors.provider_id && <span className="col-span-2 text-sm text-red-500">{errors.provider_id}</span>}
        {errors.type && <span className="col-span-2 text-sm text-red-500">{errors.type}</span>}

        {/* Row 3: Model - Only show if not Azure and not Google TTS (Google TTS uses cascading selectors) */}
        {selectedProvider?.provider !== 'azure' && !(selectedProvider?.provider === 'google' && formData.type === 'tts') && (
          <>
            <TooltipLabel
              htmlFor="model"
              label={t('aiProfiles.labels.model')}
              tooltip={t('aiProfiles.fields.model.tooltip')}
              required
            />
            <div className="col-span-1">
              {availableModels.length > 0 ? (
                <Combobox
                  single
                  options={availableModels.map(model => ({
                    value: model,
                    label: model,
                    checked: formData.model === model
                  }))}
                  onToggle={(value) => onInputChange('model', value)}
                  placeholder={loadingModels ? t('aiProfiles.modals.loadingModels') : t('aiProfiles.modals.selectModel')}
                  translations={{
                    searchOptions: t('common.search') || 'Search...',
                    noOptionsFound: t('common.noResults') || 'No options found'
                  }}
                />
              ) : (
                <Input
                  id="model"
                  type="text"
                  value={formData.model || ''}
                  onChange={(e) => onInputChange('model', e.target.value)}
                  placeholder={t('aiProfiles.fields.model.placeholder')}
                  disabled={!formData.type}
                />
              )}
            </div>
            <div className="col-span-2"></div>
            {errors.model && <span className="col-span-4 text-sm text-red-500">{errors.model}</span>}

            {/* Row 4: System Prompt - Show for 'general' and 'agent_realtime' types */}
            {(formData.type === 'general' || formData.type === 'agent_realtime') && (
              <>
                <div className="self-start pt-2">
                  <TooltipLabel
                    htmlFor="system_prompt"
                    label={t('aiProfiles.fields.systemPrompt.label')}
                    tooltip={t('aiProfiles.fields.systemPrompt.tooltip')}
                  />
                </div>
                <div className="col-span-3">
                  <Textarea
                    id="system_prompt"
                    value={formData.system_prompt || ''}
                    onChange={(e) => onInputChange('system_prompt', e.target.value)}
                    placeholder={t('aiProfiles.fields.systemPrompt.placeholder')}
                    rows={4}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </FormBox>
  );
}

